import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints } from '../../config/api';

function SearchBar({ 
  placeholder = "Enter your location or search by specialty...",
  showExamples = false,
  size = "large", // "large" or "compact"
  onSearch = null, // Optional callback for custom search handling
  initialValue = "",
  className = ""
}) {
  const [searchQuery, setSearchQuery] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locationStatus, setLocationStatus] = useState('idle'); // 'idle', 'requesting', 'success', 'error'
  const [locationError, setLocationError] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [lastKnownLocation, setLastKnownLocation] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [userCity, setUserCity] = useState('');
  const navigate = useNavigate();

  // US state abbreviations mapping
  const stateAbbreviations = {
    'alabama': 'al', 'alaska': 'ak', 'arizona': 'az', 'arkansas': 'ar', 'california': 'ca',
    'colorado': 'co', 'connecticut': 'ct', 'delaware': 'de', 'florida': 'fl', 'georgia': 'ga',
    'hawaii': 'hi', 'idaho': 'id', 'illinois': 'il', 'indiana': 'in', 'iowa': 'ia',
    'kansas': 'ks', 'kentucky': 'ky', 'louisiana': 'la', 'maine': 'me', 'maryland': 'md',
    'massachusetts': 'ma', 'michigan': 'mi', 'minnesota': 'mn', 'mississippi': 'ms', 'missouri': 'mo',
    'montana': 'mt', 'nebraska': 'ne', 'nevada': 'nv', 'new hampshire': 'nh', 'new jersey': 'nj',
    'new mexico': 'nm', 'new york': 'ny', 'north carolina': 'nc', 'north dakota': 'nd', 'ohio': 'oh',
    'oklahoma': 'ok', 'oregon': 'or', 'pennsylvania': 'pa', 'rhode island': 'ri', 'south carolina': 'sc',
    'south dakota': 'sd', 'tennessee': 'tn', 'texas': 'tx', 'utah': 'ut', 'vermont': 'vt',
    'virginia': 'va', 'washington': 'wa', 'west virginia': 'wv', 'wisconsin': 'wi', 'wyoming': 'wy'
  };

  // Common cities with default states
  const commonCitiesWithStates = {
    // Top 200 US Cities
    'new york': { state: 'ny', needsState: false },
    'los angeles': { state: 'ca', needsState: false },
    'chicago': { state: 'il', needsState: false },
    'houston': { state: 'tx', needsState: false },
    'phoenix': { state: 'az', needsState: false },
    'philadelphia': { state: 'pa', needsState: false },
    'san antonio': { state: 'tx', needsState: false },
    'san diego': { state: 'ca', needsState: false },
    'dallas': { state: 'tx', needsState: false },
    'san jose': { state: 'ca', needsState: false },
    'austin': { state: 'tx', needsState: false },
    'jacksonville': { state: 'fl', needsState: false },
    'fort worth': { state: 'tx', needsState: false },
    'columbus': { state: 'oh', needsState: false },
    'indianapolis': { state: 'in', needsState: false },
    'charlotte': { state: 'nc', needsState: false },
    'san francisco': { state: 'ca', needsState: false },
    'seattle': { state: 'wa', needsState: false },
    'denver': { state: 'co', needsState: false },
    'oklahoma city': { state: 'ok', needsState: false },
    'nashville': { state: 'tn', needsState: false },
    'el paso': { state: 'tx', needsState: false },
    'washington': { state: 'dc', needsState: false },
    'boston': { state: 'ma', needsState: false },
    'las vegas': { state: 'nv', needsState: false },
    'portland': { state: 'or', needsState: false },
    'detroit': { state: 'mi', needsState: false },
    'louisville': { state: 'ky', needsState: false },
    'memphis': { state: 'tn', needsState: false },
    'baltimore': { state: 'md', needsState: false },
    'milwaukee': { state: 'wi', needsState: false },
    'albuquerque': { state: 'nm', needsState: false },
    'fresno': { state: 'ca', needsState: false },
    'tucson': { state: 'az', needsState: false },
    'sacramento': { state: 'ca', needsState: false },
    'kansas city': { state: 'mo', needsState: false },
    'mesa': { state: 'az', needsState: false },
    'atlanta': { state: 'ga', needsState: false },
    'omaha': { state: 'ne', needsState: false },
    'colorado springs': { state: 'co', needsState: false },
    'raleigh': { state: 'nc', needsState: false },
    'virginia beach': { state: 'va', needsState: false },
    'long beach': { state: 'ca', needsState: false },
    'miami': { state: 'fl', needsState: false },
    'oakland': { state: 'ca', needsState: false },
    'minneapolis': { state: 'mn', needsState: false },
    'tulsa': { state: 'ok', needsState: false },
    'bakersfield': { state: 'ca', needsState: false },
    'wichita': { state: 'ks', needsState: false },
    'tampa': { state: 'fl', needsState: false },
    // Ambiguous cities that exist in multiple states - require state specification
    // NOTE: Do not add duplicates of cities already listed above (columbus, kansas city, etc.)
    // Those default to their most common state. Users can type "Columbus, GA" for other variants.
    'springfield': { state: 'il', needsState: true }, // Multiple Springfields
    'aurora': { state: 'co', needsState: true }, // Aurora CO and Aurora IL
    'glendale': { state: 'ca', needsState: true }, // Glendale CA and Glendale AZ
    'pasadena': { state: 'ca', needsState: true } // Pasadena CA and Pasadena TX
  };

  // Load cached location and city on component mount
  useEffect(() => {
    const cached = localStorage.getItem('therapylist_last_location');
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        // Only use if less than 24 hours old
        if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
          setLastKnownLocation(parsed);
          // Also get the cached city name if available
          const cachedCity = localStorage.getItem('therapylist_last_city');
          if (cachedCity) {
            setUserCity(cachedCity);
          }
        } else {
          localStorage.removeItem('therapylist_last_location');
          localStorage.removeItem('therapylist_last_city');
        }
      } catch (e) {
        localStorage.removeItem('therapylist_last_location');
        localStorage.removeItem('therapylist_last_city');
      }
    }
  }, []);

  // Debounced suggestions
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuggestions(searchQuery);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update search query when initialValue changes
  useEffect(() => {
    setSearchQuery(initialValue);
  }, [initialValue]);

  // Reverse geocode coordinates to get city name
  const reverseGeocode = async (lat, lng) => {
    try {
      // Use a simple reverse geocoding service 
      const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`);
      const data = await response.json();
      
      if (data.city && data.principalSubdivisionCode) {
        const cityName = `${data.city}, ${data.principalSubdivisionCode}`;
        setUserCity(cityName);
        localStorage.setItem('therapylist_last_city', cityName);
        return cityName;
      }
    } catch (error) {
      console.warn('Failed to reverse geocode:', error);
    }
    return null;
  };

  // Format city name for URL (kebab-case)
  const formatCityForUrl = (cityName) => {
    return cityName.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Remove multiple consecutive hyphens
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  // Parse search query to detect search type and extract components
  const parseSearchQuery = (query) => {
    const trimmedQuery = query.trim().toLowerCase();
    
    // Check if it's a zip code (5 digits)
    const zipPattern = /^\d{5}$/;
    if (zipPattern.test(trimmedQuery)) {
      return {
        type: 'location',
        zipCode: trimmedQuery,
        query: trimmedQuery
      };
    }
    
    // Check if it looks like a city, state format with comma
    const cityStatePattern = /^(.+),\s*([a-z]{2}|[a-z\s]+)$/i;
    const cityStateMatch = trimmedQuery.match(cityStatePattern);
    
    if (cityStateMatch) {
      const [, cityPart, statePart] = cityStateMatch;
      const city = cityPart.trim();
      const state = statePart.trim();
      
      // Convert state name to abbreviation if needed
      const stateAbbr = stateAbbreviations[state] || state;
      
      return {
        type: 'city',
        city: city,
        state: stateAbbr.toUpperCase(),
        cityUrl: formatCityForUrl(city),
        stateUrl: stateAbbr.toLowerCase()
      };
    }
    
    // Check if it looks like a city (state) format with parentheses
    const cityStateParenPattern = /^(.+)\s*\(([a-z]{2}|[a-z\s]+)\)$/i;
    const cityStateParenMatch = trimmedQuery.match(cityStateParenPattern);
    
    if (cityStateParenMatch) {
      const [, cityPart, statePart] = cityStateParenMatch;
      const city = cityPart.trim();
      const state = statePart.trim();
      
      // Convert state name to abbreviation if needed
      const stateAbbr = stateAbbreviations[state] || state;
      
      return {
        type: 'location', // Use 'location' type for more reliable backend handling
        city: city,
        state: stateAbbr.toUpperCase(),
        query: `${city}, ${stateAbbr.toUpperCase()}`, // Format for backend
        cityUrl: formatCityForUrl(city),
        stateUrl: stateAbbr.toLowerCase()
      };
    }
    
    // Check if it's a known city
    if (commonCitiesWithStates[trimmedQuery]) {
      const cityInfo = commonCitiesWithStates[trimmedQuery];
      return {
        type: 'city',
        city: trimmedQuery,
        state: cityInfo.state.toUpperCase(),
        cityUrl: formatCityForUrl(trimmedQuery),
        stateUrl: cityInfo.state.toLowerCase(),
        needsState: cityInfo.needsState
      };
    }
    
    // Check if it's a state (full name or abbreviation)
    if (stateAbbreviations[trimmedQuery] || Object.values(stateAbbreviations).includes(trimmedQuery.toLowerCase())) {
      // Convert to uppercase abbreviation for consistency
      const stateAbbr = stateAbbreviations[trimmedQuery] || trimmedQuery.toUpperCase();
      return {
        type: 'state',
        state: stateAbbr,
        query: stateAbbr
      };
    }
    
    // Check if it's a specialty
    const commonSpecialties = ['anxiety', 'depression', 'couples therapy', 'adhd', 'trauma', 'teen therapy', 'family therapy', 'addiction', 'grief', 'eating disorders'];
    if (commonSpecialties.includes(trimmedQuery)) {
      return {
        type: 'specialty',
        specialty: trimmedQuery
      };
    }
    
    // Default to general search
    return {
      type: 'general',
      query: trimmedQuery
    };
  };

  // Handle search form submission
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    // If onSearch callback is provided, use it instead of navigation
    if (onSearch) {
      onSearch(searchQuery, parseSearchQuery(searchQuery));
      return;
    }
    
    const searchData = parseSearchQuery(searchQuery);
    
    if (searchData.type === 'city') {
      if (searchData.needsState) {
        navigate(`/find-therapists?location=${encodeURIComponent(searchQuery)}`);
      } else {
        navigate(`/therapists/${searchData.stateUrl}/${searchData.cityUrl}`);
      }
    } else if (searchData.type === 'location' && searchData.zipCode) {
      // Handle zip code searches
      navigate(`/find-therapists?location=${encodeURIComponent(searchData.zipCode)}`);
    } else if (searchData.type === 'location' && searchData.city && searchData.state) {
      // Handle formatted location searches (like "City (State)" format)
      navigate(`/find-therapists?location=${encodeURIComponent(searchData.query)}`);
    } else if (searchData.type === 'state') {
      // Handle state searches - use state abbreviation for consistent backend processing
      navigate(`/find-therapists?location=${encodeURIComponent(searchData.state)}`);
    } else if (searchData.type === 'specialty') {
      navigate(`/find-therapists?specialty=${encodeURIComponent(searchData.specialty)}`);
    } else {
      navigate(`/find-therapists?q=${encodeURIComponent(searchData.query)}`);
    }
    
    setShowSuggestions(false);
  };

  // Fetch search suggestions
  const fetchSuggestions = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await fetch(`${endpoints.search.suggestions}?q=${encodeURIComponent(query)}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSuggestions(data.suggestions.slice(0, 5)); // Limit to 5 suggestions
          setShowSuggestions(data.suggestions.length > 0);
        }
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.label);
    setShowSuggestions(false);
    
    // If onSearch callback is provided, use it
    if (onSearch) {
      onSearch(suggestion.label, parseSearchQuery(suggestion.label));
      return;
    }
    
    // Handle suggestions based on their type and content
    // Check for ZIP code suggestions first (they have type 'location' and label contains ZIP code indicators)
    if (suggestion.type === 'location' && (suggestion.label.includes('(ZIP code)') || suggestion.label.includes('ZIP CODE'))) {
      // Handle ZIP code searches - use suggestion.value which contains clean ZIP code
      navigate(`/find-therapists?location=${encodeURIComponent(suggestion.value)}`);
    } else if (suggestion.type === 'specialization') {
      // Handle specialty suggestions
      navigate(`/find-therapists?specialty=${encodeURIComponent(suggestion.value)}`);
    } else if (suggestion.type === 'location') {
      // Check if this is a state suggestion (label format: "State (Abbr)")
      const statePattern = /^(.+)\s*\(([A-Z]{2})\)$/;
      const stateMatch = suggestion.label.match(statePattern);
      
      if (stateMatch) {
        // This is a state suggestion - use the abbreviation value for the search
        console.log('State suggestion detected:', suggestion.value); // Debug log
        navigate(`/find-therapists?location=${encodeURIComponent(suggestion.value)}`);
      } else {
        // For non-state location suggestions, use the value if it's just a state abbreviation
        // This handles cases where the backend sends state abbreviations directly
        const isStateAbbr = /^[A-Z]{2}$/;
        if (isStateAbbr.test(suggestion.value)) {
          console.log('State abbreviation detected:', suggestion.value); // Debug log
          navigate(`/find-therapists?location=${encodeURIComponent(suggestion.value)}`);
        } else {
          // Handle other location suggestions (cities)
          const searchData = parseSearchQuery(suggestion.label);
          
          if (searchData.type === 'city') {
            if (searchData.needsState) {
              navigate(`/find-therapists?location=${encodeURIComponent(suggestion.label)}`);
            } else if (searchData.city && searchData.state) {
              navigate(`/therapists/${searchData.stateUrl}/${searchData.cityUrl}`);
            } else {
              navigate(`/find-therapists?location=${encodeURIComponent(suggestion.label)}`);
            }
          } else if (searchData.type === 'location' && searchData.city && searchData.state) {
            // Handle formatted location searches (like "City (State)" format)
            navigate(`/find-therapists?location=${encodeURIComponent(searchData.query)}`);
          } else {
            // Default location search - use value instead of label for better backend processing
            navigate(`/find-therapists?location=${encodeURIComponent(suggestion.value)}`);
          }
        }
      }
    } else {
      // General search fallback
      navigate(`/find-therapists?q=${encodeURIComponent(suggestion.label)}`);
    }
  };

  // Detect if user is on iOS Safari
  const isIOSSafari = () => {
    const userAgent = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(userAgent) && /Safari/.test(userAgent) && !/CriOS|FxiOS|OPiOS|mercury/.test(userAgent);
  };

  // Detect if user is on any mobile browser
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Check if site is running on HTTPS
  const isHTTPS = () => {
    return window.location.protocol === 'https:' || window.location.hostname === 'localhost';
  };

  // Enhanced geolocation handling
  const handleUseMyLocation = async () => {
    console.log('🔍 Geolocation request initiated');
    
    // Check for geolocation support
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      setLocationStatus('error');
      console.error('❌ Geolocation not supported');
      return;
    }

    // Check for HTTPS requirement (important for all mobile browsers)
    if (!isHTTPS() && isMobile()) {
      setLocationError('Location services require a secure connection (HTTPS). Please access the site via HTTPS.');
      setLocationStatus('error');
      console.error('❌ HTTPS required for mobile geolocation');
      return;
    }

    // For iOS Safari, skip permission checks and go directly to location request
    // to maintain user gesture chain
    if (isIOSSafari()) {
      console.log('🍎 iOS Safari detected - requesting location directly');
      requestNewLocation();
      return;
    }

    // For other browsers, check permissions if available
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        console.log('🔒 Geolocation permission status:', permission.state);
        
        if (permission.state === 'denied') {
          setLocationError('Location access was previously denied. Please enable location permissions in your browser settings and refresh the page.');
          setLocationStatus('error');
          return;
        }
      } catch (permissionError) {
        console.warn('⚠️ Could not check permission status:', permissionError);
        // Continue anyway - some browsers don't support permission query
      }
    }

    // Request location for non-iOS Safari browsers
    requestNewLocation();
  };

  const requestNewLocation = () => {
    setLocationStatus('requesting');
    setLocationError('');
    console.log('📍 Requesting current location...');

    const options = {
      enableHighAccuracy: true,
      timeout: isIOSSafari() ? 25000 : isMobile() ? 20000 : 15000, // Longer timeout for mobile
      maximumAge: isMobile() ? 60000 : 300000 // Shorter cache for mobile (1 minute vs 5 minutes)
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        console.log('✅ Location obtained:', {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        
        const { latitude, longitude } = position.coords;
        const locationData = {
          lat: latitude,
          lng: longitude,
          timestamp: Date.now()
        };
        
        // Cache the location
        localStorage.setItem('therapylist_last_location', JSON.stringify(locationData));
        setLastKnownLocation(locationData);
        setLocationStatus('success');
        
        // Try to get city name
        await reverseGeocode(latitude, longitude);
        
        // Use coordinates to search for therapists
        if (onSearch) {
          onSearch('Current Location', {
            type: 'location',
            lat: latitude,
            lng: longitude,
            query: 'Current Location'
          });
        } else {
          navigate(`/find-therapists?lat=${latitude}&lng=${longitude}`);
        }
        
        // Update search query to show user something happened
        setSearchQuery('Current Location');
        setTimeout(() => {
          setLocationStatus('idle');
        }, 3000);
      },
      (error) => {
        console.error('❌ Geolocation error:', {
          code: error.code,
          message: error.message,
          isIOSSafari: isIOSSafari(),
          isMobile: isMobile(),
          isHTTPS: isHTTPS(),
          userAgent: navigator.userAgent
        });
        
        setLocationStatus('error');
        
        // Enhanced error messages for different browsers
        switch (error.code) {
          case error.PERMISSION_DENIED:
            if (isIOSSafari()) {
              setLocationError('Location access was denied. Please tap the location icon (🌐) in Safari\'s address bar and select "Allow", or go to Settings > Safari > Location > Allow.');
            } else if (isMobile()) {
              setLocationError('Location access was denied. Please check your browser settings and allow location access for this site.');
            } else {
              setLocationError('Location access denied. Please enable location permissions and try again.');
            }
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information is unavailable. Please check your device\'s location services and try again, or search manually.');
            break;
          case error.TIMEOUT:
            if (isIOSSafari()) {
              setLocationError('Location request timed out. This can happen on iOS Safari. Please try again or search by city/ZIP code.');
            } else {
              setLocationError('Location request timed out. Please try again or search manually.');
            }
            break;
          default:
            if (isIOSSafari()) {
              setLocationError('Unable to get your location. Make sure Location Services are enabled in Settings > Privacy & Security > Location Services > Safari > While Using App.');
            } else {
              setLocationError('Unable to get your location. Please try searching by city or ZIP code.');
            }
            break;
        }
        
        setTimeout(() => {
          setLocationStatus('idle');
          setLocationError('');
        }, 12000); // Longer timeout for error messages
      },
      options
    );
  };



  // Size-based styling
  const sizeStyles = {
    large: {
      container: "p-2",
      input: "px-6 py-4 text-lg",
      button: "px-8 py-4 font-semibold",
      suggestions: "max-h-60"
    },
    compact: {
      container: "p-1",
      input: "px-4 py-2 text-sm",
      button: "px-4 py-2 text-sm font-medium",
      suggestions: "max-h-40"
    }
  };

  const styles = sizeStyles[size];

  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSearch} className={`rounded-2xl bg-white shadow-[0_12px_32px_rgba(37,78,112,0.1)] border border-[#254e70]/10 ${styles.container}`}>
        <div className="flex flex-col gap-3 md:flex-row md:gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder={placeholder}
              className={`w-full bg-transparent text-[#254e70] placeholder-[#254e70]/40 focus:outline-none ${styles.input}`}
            />
            
            {/* Enhanced Near Me Button */}
            <button
              type="button"
              onClick={handleUseMyLocation}
              disabled={locationStatus === 'requesting'}
              className={`hidden sm:flex absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-2 rounded-xl text-xs font-semibold transition-all duration-300 hover:scale-105 active:scale-95 items-center gap-1.5 shadow-lg border-2 ${
                locationStatus === 'requesting' ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 text-blue-600 cursor-not-allowed animate-pulse' :
                locationStatus === 'success' ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-700 hover:from-green-100 hover:to-emerald-100' :
                locationStatus === 'error' ? 'bg-gradient-to-r from-red-50 to-rose-50 border-red-200 text-red-700 hover:from-red-100 hover:to-rose-100' :
                'bg-gradient-to-r from-[#9dacff]/10 to-[#9dacff]/20 border-[#9dacff]/30 text-[#254e70] hover:from-[#9dacff]/20 hover:to-[#9dacff]/40 hover:border-[#9dacff]/50 hover:shadow-xl'
              }`}
              title={
                locationStatus === 'error' ? locationError :
                locationStatus === 'success' ? 'Location found!' :
                locationStatus === 'requesting' ? 'Getting your location...' :
                'Find therapists near your current location'
              }
            >
              {locationStatus === 'requesting' ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-current border-t-transparent"></div>
                  <span className="hidden sm:inline">Finding...</span>
                </>
              ) : locationStatus === 'success' ? (
                <>
                  <svg className="w-3.5 h-3.5 animate-bounce" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="hidden sm:inline">Found!</span>
                </>
              ) : locationStatus === 'error' ? (
                <>
                  <svg className="w-3.5 h-3.5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="hidden sm:inline">Try Again</span>
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5 transition-transform group-hover:scale-110" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  <span className="hidden sm:inline">Near Me</span>
                </>
              )}
            </button>
            
            {/* Search Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
              <div className={`absolute top-full left-0 right-0 bg-white border border-[#254e70]/10 rounded-2xl shadow-[0_12px_32px_rgba(37,78,112,0.15)] mt-2 z-50 overflow-hidden ${styles.suggestions} overflow-y-auto`}>
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left px-4 py-3 hover:bg-[#e4fde1] transition-colors flex items-center gap-3 border-b border-[#254e70]/5 last:border-b-0"
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      suggestion.type === 'location' ? 'bg-[#9dacff]/20 text-[#9dacff]' : 
                      suggestion.type === 'specialization' ? 'bg-[#e4fde1] text-[#254e70]' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {suggestion.type === 'location' ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                      ) : suggestion.type === 'specialization' ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9 12a3 3 0 116 0 3 3 0 01-6 0zm3-1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a4 4 0 00-2 7.5V16a1 1 0 102 0v-3.5A4 4 0 0011 5z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-[#254e70]">{suggestion.label}</div>
                      <div className="text-xs text-[#254e70]/60 capitalize">{suggestion.type}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {/* Loading indicator for search suggestions */}
            {loading && searchQuery.length >= 2 && locationStatus !== 'requesting' && (
              <div className="absolute top-1/2 right-4 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#254e70]"></div>
              </div>
            )}
          </div>
          
          <button
            type="submit"
            className={`rounded-xl bg-[#254e70] text-white transition-all duration-200 hover:bg-[#1d3e5a] hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 ${styles.button}`}
          >
            Find Therapists
          </button>
        </div>
      </form>


      
      {/* Enhanced Location Error Message */}
      {locationError && locationStatus === 'error' && (
        <div className="mt-3 p-4 bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-400 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-red-800 mb-1">Location Not Available</h4>
              <p className="text-red-700 text-sm mb-2">{locationError}</p>
              
              {/* iOS Safari specific instructions */}
              {isIOSSafari() && (
                <div className="text-xs text-red-600 mb-2">
                  <p className="mb-1 font-medium">For iPhone/iPad Safari:</p>
                  <ol className="list-decimal list-inside space-y-0.5 ml-2">
                    <li>Look for the location icon (🌐) in the address bar and tap it</li>
                    <li>Select "Allow" for location permissions</li>
                    <li>If no icon appears, try refreshing the page first</li>
                    <li>Alternatively, go to Settings → Safari → Location → Allow</li>
                  </ol>
                  <p className="mt-2 text-xs font-medium">⚠️ Make sure Location Services are enabled in Settings → Privacy & Security → Location Services → Safari → While Using App</p>
                </div>
              )}
              
              {/* General browser instructions */}
              {!isIOSSafari() && locationError.includes('denied') && (
                <div className="text-xs text-red-600">
                  <p className="mb-1">To enable location access:</p>
                  <ol className="list-decimal list-inside space-y-0.5 ml-2">
                    <li>Click the location icon in your browser's address bar</li>
                    <li>Select "Allow" for location permissions</li>
                    <li>Try the "Near Me" button again</li>
                  </ol>
                </div>
              )}
              
              {/* HTTPS warning */}
              {!isHTTPS() && (
                <div className="text-xs text-red-600 mt-2 p-2 bg-red-100 rounded">
                  <p className="font-medium">⚠️ Security Notice:</p>
                  <p>Location services require a secure (HTTPS) connection. Consider accessing the site via HTTPS for full functionality.</p>
                </div>
              )}
              
              {/* Alternative suggestions */}
              <div className="text-xs text-red-600 mt-2 p-2 bg-red-50 rounded border border-red-200">
                <p className="font-medium mb-1">Alternative options:</p>
                <ul className="list-disc list-inside space-y-0.5 ml-2">
                  <li>Search by city name (e.g., "San Francisco, CA")</li>
                  <li>Enter your ZIP code (e.g., "94102")</li>
                  <li>Browse therapists by specialty instead</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      


      {/* Enhanced Search examples/hints */}
      {showExamples && (
        <div className="mt-4 text-center">
          <p className="text-sm text-[#254e70]/60 mb-3">Try searching for:</p>
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {[
              'San Francisco, CA',
              'Anxiety therapy',
              'Couples counseling',
              'Chicago therapists'
            ].map((example) => (
              <button
                key={example}
                type="button"
                onClick={() => {
                  setSearchQuery(example);
                  setShowSuggestions(false);
                }}
                className="text-xs bg-[#e4fde1] text-[#254e70] px-3 py-2 rounded-full hover:bg-[#9dacff]/20 transition-all duration-200 hover:scale-105 font-medium border border-transparent hover:border-[#9dacff]/20"
              >
                {example}
              </button>
            ))}
          </div>

        </div>
      )}
    </div>
  );
}

export default SearchBar; 